import { JournalManifest } from '../types/journal';

const XML_NAMESPACE = 'bifrost:journal:manifest';

function wrapInXml(data: JournalManifest): string {
  return `<manifest xmlns="${XML_NAMESPACE}"><![CDATA[${JSON.stringify(data)}]]></manifest>`;
}

function parseFromXml(xml: string): JournalManifest | null {
  const match = xml.match(/<!\[CDATA\[(.*?)\]\]>/);
  if (!match?.[1]) return null;
  try {
    return JSON.parse(match[1]) as JournalManifest;
  } catch {
    return null;
  }
}

/**
 * A manifest is identified by its worksheet id where one is recorded, falling
 * back to the name for manifests written before ids were stored. Matching on
 * either lets a renamed sheet keep its manifest instead of orphaning it.
 */
function matchesKey(manifest: JournalManifest, key: string): boolean {
  return manifest.worksheetId === key || manifest.worksheetName === key;
}

export class CustomXmlService {
  /** Look a manifest up by worksheet id or worksheet name. */
  async getManifest(key: string): Promise<JournalManifest | null> {
    const all = await this.getAllManifests();
    return all.find(m => matchesKey(m, key)) ?? null;
  }

  async setManifest(manifest: JournalManifest): Promise<void> {
    await this.replaceManifests([], [manifest]);
  }

  /** Remove a manifest by worksheet id or worksheet name. */
  async removeManifest(key: string): Promise<void> {
    await this.replaceManifests([key], []);
  }

  /**
   * Apply a batch of manifest deletions and upserts in a single Excel context.
   * Upserts replace any part already matching the same worksheet, so callers
   * never need a separate read-then-delete pass.
   */
  async replaceManifests(removeKeys: string[], upserts: JournalManifest[]): Promise<void> {
    const keysToDrop = [
      ...removeKeys,
      ...upserts.map(m => m.worksheetId ?? m.worksheetName),
    ];
    if (keysToDrop.length === 0 && upserts.length === 0) return;

    await Excel.run(async (context) => {
      const items = context.workbook.customXmlParts.getByNamespace(XML_NAMESPACE);
      items.load('items');
      await context.sync();

      const xmlResults = items.items.map(item => item.getXml());
      await context.sync();

      for (let i = 0; i < items.items.length; i++) {
        const xmlResult = xmlResults[i];
        const item = items.items[i];
        if (!xmlResult || !item) continue;
        const manifest = parseFromXml(xmlResult.value);
        if (manifest && keysToDrop.some(key => matchesKey(manifest, key))) {
          item.delete();
        }
      }
      await context.sync();

      for (const manifest of upserts) {
        context.workbook.customXmlParts.add(wrapInXml(manifest));
      }
      await context.sync();
    });
  }

  async getAllManifests(): Promise<JournalManifest[]> {
    return Excel.run(async (context) => {
      const items = context.workbook.customXmlParts.getByNamespace(XML_NAMESPACE);
      items.load('items');
      await context.sync();

      const xmlResults = items.items.map(item => item.getXml());
      await context.sync();

      const manifests: JournalManifest[] = [];
      for (const xmlResult of xmlResults) {
        const manifest = parseFromXml(xmlResult.value);
        if (manifest) manifests.push(manifest);
      }
      return manifests;
    });
  }
}
